/**
 * ClinicalDayView — Main orchestrator for the clinical day view dashboard.
 *
 * Left-column flex container for patient sections. Fetches all data via
 * useDashboardData(). Renders section components (pinned, recently seen,
 * expected this week, attention items) as they become available.
 *
 * Phase 1: Pinned patients section only (placeholder for others).
 * Phase 2+: Additional sections populated incrementally.
 *
 * The right column (inline Activity Bar) is rendered by AppShell — this
 * component only controls the left-column content.
 */
import React, { useEffect } from "react";
import { useDashboardData } from "../../hooks/useDashboardData";
import { useDashboardPrefs } from "../../hooks/useDashboardPrefs";
import { useAppShellContext } from "../../contexts/AppShellContext";
import PinnedPatientsSection from "./PinnedPatientsSection";
import RecentlySeenSection from "./RecentlySeenSection";
import ExpectedThisWeekSection from "./ExpectedThisWeekSection";
import "../../css/clinical-day-view.css";
import { afterDashboardPaint } from '../../utils/dashboardPerformance';

const ClinicalDayView = ({ timing }) => {
    const { pinnedPatients, allPatients, hasLoaded, isLoading, isRefreshing, error } = useDashboardData();
    const dashboardPrefs = useDashboardPrefs();
    const { clearSelectedPatient } = useAppShellContext();

    useEffect(() => { timing?.stage('shellReady'); }, [timing]);
    useEffect(() => {
        if ((!hasLoaded && !error) || isLoading) return undefined;
        return afterDashboardPaint(() => timing?.report({
            patientCount: allPatients.length, outcome: error ? 'error' : 'loaded', isRefreshing,
        }));
    }, [timing, hasLoaded, isLoading, isRefreshing, error, allPatients.length]);

    // Clear selection when leaving the dashboard
    useEffect(() => {
        return () => clearSelectedPatient();
    }, [clearSelectedPatient]);

    // Escape key clears patient selection
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") clearSelectedPatient();
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [clearSelectedPatient]);

    if (error && allPatients.length === 0) {
        return (
            <div className="clinical-day-view">
                <div className="clinical-day-view-error">
                    Failed to load dashboard data. Please refresh.
                </div>
            </div>
        );
    }

    return (
        <div className="clinical-day-view userback-ignore">
            <div className="clinical-day-view-sections">
                {!hasLoaded && !error ? (
                    <div className="clinical-day-view-loading" role="status">
                        Loading patient summaries...
                    </div>
                ) : <>
                <ExpectedThisWeekSection allPatients={allPatients} dashboardPrefs={dashboardPrefs} />
                <RecentlySeenSection allPatients={allPatients} dashboardPrefs={dashboardPrefs} />
                <PinnedPatientsSection patients={pinnedPatients} dashboardPrefs={dashboardPrefs} />
                </>}
            </div>
        </div>
    );
};

export default ClinicalDayView;
